import React, { useState, useEffect } from "react";
import { Loader2, Save, CheckCircle2, AlertCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@cms/ui/components/card";
import { Button } from "@cms/ui/components/button";
import { Label } from "@cms/ui/components/label";
import { Alert, AlertDescription } from "@cms/ui/components/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@cms/ui/components/select";
import type { ApiClient } from "../lib/api";

// Radix Select items can't carry an empty value, so "unset" gets a sentinel.
const NO_PAGE = "__none__";

interface PageOption {
  slug: string;
  title: string;
  published?: number;
}

/**
 * "Menu page" card on the Menus overview: pick which of the client's pages
 * carries the menu block. That page becomes the table QR code's target and the
 * canonical "menu" link. Saves site_config.menu_page_slug; the site rebuilds on
 * Publish as usual.
 */
export function MenuPageSettingsCard({ api, refreshStatus }: {
  api: ApiClient;
  refreshStatus?: () => void;
}) {
  const [slug, setSlug] = useState<string | null>(null);
  const [pages, setPages] = useState<PageOption[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!api) return;
    Promise.all([
      api.request<{ config?: { menu_page_slug?: string | null } | null }>("/tenant/config"),
      api.request<{ pages?: PageOption[] }>("/tenant/pages"),
    ])
      .then(([data, pagesData]) => {
        setSlug(data.config?.menu_page_slug ?? null);
        setPages(pagesData.pages ?? []);
        setLoaded(true);
      })
      .catch((err: Error) => setNotice({ ok: false, text: err.message }));
  }, [api]);

  if (!loaded) return null;

  const save = async () => {
    setSaving(true);
    setNotice(null);
    try {
      await api.request("/tenant/config", {
        method: "PUT",
        body: JSON.stringify({ menu_page_slug: slug || null }),
      });
      setNotice({ ok: true, text: "Menu page saved. Publish the site to make it live." });
      refreshStatus?.();
    } catch (err) {
      setNotice({ ok: false, text: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-sm">Menu page</CardTitle>
        <CardDescription>
          Which page shows your menu? Add a Menu block to that page in the page editor. The table
          QR code links here, so customers always land on your latest published menu.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {notice && (
          <Alert variant={notice.ok ? "default" : "destructive"}>
            {notice.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            <AlertDescription>{notice.text}</AlertDescription>
          </Alert>
        )}
        <div className="max-w-sm space-y-1.5">
          <Label htmlFor="menu-page-slug">Menu page</Label>
          <Select
            value={slug || NO_PAGE}
            onValueChange={(value: string) => setSlug(value === NO_PAGE ? null : value)}
          >
            <SelectTrigger id="menu-page-slug">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_PAGE}>No page chosen</SelectItem>
              {pages.map((page) => (
                <SelectItem key={page.slug} value={page.slug}>
                  {page.title} {page.published ? "" : "(draft)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save menu page
        </Button>
      </CardContent>
    </Card>
  );
}
